/**
 * dashboardSignOffSnapshot.ts
 *
 * Captures each table widget's column/row labels into
 * TableGraphWidgetData.sign_off_snapshot at dashboard sign-off time (#969
 * Part F). This is the baseline the Review Changes panel
 * (utils/reviewChanges.ts) diffs the live table against after a later wave
 * refresh, to find new columns/rows.
 *
 * Mirrors dashboardSignOffPin.ts's shape deliberately: same reasons apply —
 * resolving a table's labels needs the labels themselves, and only the
 * frontend has them without a Carbon round trip from the sign-off path, so
 * this runs on the client, walks every page, and fails the whole sign-off if
 * any table can't be read (a partial baseline would misreport "no change"
 * for whatever wasn't captured).
 */

import { DashboardConfig } from '../types/gotypes'
import { walkTableGraphWidgets } from './dashboardWidgetWalk'

/** Loads a table's column and row labels, in table order. */
export type TableLabelsLoader = (job: string, tablePath: string) => Promise<{ columns: string[]; rows: string[] }>

/**
 * Returns a copy of the config with every table_graph widget's
 * sign_off_snapshot set to its table's current column/row labels.
 *
 * Widgets with no table_path (inline-spec widgets, or an already-broken
 * reference — Part E's missing-table state) are left with no snapshot: there
 * is nothing to load, and a missing table has nothing to diff once it comes
 * back under a new path (a new path is a new widget configuration, not a
 * "welcome back" comparable to the old table).
 */
export async function captureTableSnapshotsForSignOff(
  config: DashboardConfig,
  loadLabels: TableLabelsLoader,
): Promise<DashboardConfig> {
  const copy = JSON.parse(JSON.stringify(config)) as DashboardConfig
  const refs = walkTableGraphWidgets(copy).filter(ref => Boolean(ref.data.table_path))
  if (refs.length === 0) return copy

  // One load per distinct table — a dashboard often charts the same table
  // several ways, and each widget's slice of it still gets the full label set
  // (the diff itself, not the snapshot, is where a widget's row/column
  // *filter* narrows what's reported as "new" — out of scope here per the
  // module's job of capturing, not filtering).
  const byTable = new Map<string, typeof refs>()
  refs.forEach(ref => {
    const key = JSON.stringify([ref.data.job, ref.data.table_path])
    const group = byTable.get(key)
    if (group) group.push(ref)
    else byTable.set(key, [ref])
  })

  await Promise.all(Array.from(byTable.values()).map(async group => {
    const { job, table_path: tablePath } = group[0].data
    const labels = await loadLabels(job, tablePath)
    group.forEach(({ data }) => {
      data.sign_off_snapshot = { columns: labels.columns, rows: labels.rows }
    })
  }))

  return copy
}
